/**
 * @wtc/support-chat - Main exports
 * 
 * A reusable support chat package that integrates Slack as the backend,
 * with a React chat widget and Vercel serverless functions.
 */

// Components
export { SupportChatWidget } from './components/SupportChatWidget';
export { ThreadList } from './components/ThreadList';
// ChatHeader, ChatMessage, and ChatInput are internal components used by SupportChatWidget
// They are not exported to avoid naming conflicts with types

// Hooks
export { useSupportChat } from './hooks/useSupportChat';
export { useMessagePolling } from './hooks/useMessagePolling';
export { useThreadManagement } from './hooks/useThreadManagement';
export { useThreadStorage } from './hooks/useThreadStorage';

// API Handlers - Server-side only
// These should only be imported in Next.js API route files (server-side)
// Do not import these in client-side components
// Import directly from '@wtc/support-chat/api/handlers' in your API routes

// Types - Export from centralized types index
export type {
  // Core types
  SupportChatConfig,
  SupportChatUser,
  ChatMessage,
  FileAttachment,
  ThreadInfo,
  MessageSender,
  MessageSenderType,

  // Opt-in feature options (Hub-only via props)
  SupportChatOfflineFallbackOptions,
  SupportChatRatingOptions,
  SupportChatConvertContext,
  SupportChatConvertResult,
  
  // Request/Response types
  CreateThreadRequest,
  CreateThreadResponse,
  CreateThreadErrorResponse,
  SendMessageRequest,
  SendMessageResponse,
  SendMessageErrorResponse,
  GetMessagesRequest,
  GetMessagesResponse,
  GetMessagesErrorResponse,
  
  // API Response types
  ApiSuccessResponse,
  ApiErrorResponse,
  
  // Utility types
  Timestamp,
  ThreadId,
  ChannelId,
  MessageId,
  CognitoSub,
  
  // Cookie storage types (exported as type only, not value)
  
  // Slack types
  SlackMessageMetadata,
  SlackMessageMetadataPayload,
  SlackThreadMessage,
  SlackThreadResponse,
  SlackPostMessageResponse,
  SlackPostMessageSuccessResponse,
  SlackPostMessageErrorResponse,
  FormattedSlackMessage,
  SlackUserProfile,
  SlackFile,
  
  // Cognito types
  CognitoUserAttribute,
  CognitoUserAttributes,
  
  // Thread storage types
  SupportThread,
  UpdateThreadListRequest,
  UpdateThreadListResponse,
} from './types';

// Type helpers
export {
  isErrorResponse,
  isValidCreateThreadResponse,
  isSendMessageSuccess,
  toThreadId,
  toChannelId,
  toMessageId,
  toCognitoSub,
  toTimestamp,
  isSlackSuccessResponse,
  isSlackPostMessageSuccess,
  hasSlackMetadata,
  parseSlackMetadata,
  toSlackChannelId,
  toSlackThreadTs,
  toSlackMessageTs,
  isCognitoUserAttribute,
} from './types';

// Cognito client utilities (client-side)
export {
  getUserSupportThreads,
  updateUserSupportThreads,
  addUserSupportThread,
  removeUserSupportThread,
  findUserSupportThread,
} from './lib/cognito-client';

// Cookie storage utilities (for unauthenticated users)
export {
  getCookieSupportThreads,
  updateCookieSupportThreads,
  addCookieSupportThread,
  removeCookieSupportThread,
  findCookieSupportThread,
} from './lib/cookie-storage';
export type { CookieStorageConfig } from './lib/cookie-storage';

// Config
export { DEFAULT_CONFIG } from './config/default-config';

